<template>
  <v-container class="custom-container pt-2 pb-0">
    <v-sheet
      :theme="getThemeName(subject.slug)"
      v-for="(subject, index) in sortedSubjects"
      :key="index"
      class="subject-list-row my-0 subject-list-wrapper hover-bg subject-border pa-4 px-sm-8 py-sm-6"
    >
      <h2 class="subject-list-row--heading text-primary mb-4">
        <router-link :to="'/videos/' + subject.slug">
          {{ subject.category }}<span>{{ t(l.subject.explore_all) }}</span>
        </router-link>
        <FontAwesomeIcon icon="fa-chevron-right" size="xs" />
      </h2>
      <v-lazy :options="{ threshold: 0.2 }" :transition="false">
        <SingleSubjectVideoList :subject-id="subject.id"></SingleSubjectVideoList>
      </v-lazy>
    </v-sheet>
  </v-container>
</template>

<script setup lang="ts">
import orderBy from 'lodash/orderBy';
import { computed } from 'vue';
import { useLocale } from '@/plugins/translation';
import useGuiStore from '@/store/gui';
import SingleSubjectVideoList from './SingleSubjectVideoList.vue';
import useSubjectsWithVideos from '@/composables/use-subjects-with-videos';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import type { Category } from '@ilteducation/content-ref-data-types';

const { t, l } = useLocale();
const { subjectsWithVideos } = useSubjectsWithVideos();
const sortedSubjects = computed<Category[]>(() => orderBy(subjectsWithVideos.value, 'category'));
const { getThemeName } = useGuiStore();
</script>

<style lang="scss" scoped>
@use '@/foundations/theme/settings';
@use 'sass:map';

.subject-list-row {
  .container {
    max-width: none;
  }
}

.subject-list-wrapper {
  width: 100%;

  &.hover-bg {
    &:hover {
      background: rgba(var(--v-theme-surface-container-low));
      box-shadow: 0 10px 20px 0 rgba(18, 43, 64, 0.1);
    }
  }
}

.subject-list-row {
  &--heading {
    display: inline-block;
    font-size: 1.125rem;
    margin: 0 0 5px;

    span {
      display: none;
    }

    @media #{map.get(settings.$display-breakpoints, 'md-and-up')} {
      font-size: 1.375rem;
      vertical-align: text-bottom;

      span {
        display: inline-block;
        font-family: settings.$body-font-family;
        font-size: 0.9375rem;
        font-weight: 400;
        line-height: 16px;
        letter-spacing: 0.1px;
        white-space: nowrap;
        margin-left: 12px;
        transition: max-width 0.35s;
        overflow: hidden;
        max-width: 0;
        margin-bottom: -3px;
      }

      &:hover {
        span {
          max-width: 7.5rem;
        }
      }
    }
  }
}

.subject-border {
  position: relative;
  border-bottom: 1px solid rgb(var(--v-theme-gray), 0.2);

  @media #{map.get(settings.$display-breakpoints, 'sm-and-down')} {
    border-top: 6px solid rgba(var(--v-theme-subject-original));
  }

  @media #{map.get(settings.$display-breakpoints, 'md-and-up')} {
    &:before {
      display: block;
      content: '';
      position: absolute;
      border-left: 10px solid rgba(var(--v-theme-subject-original));
      width: 25px;
      height: 100%;
      left: 0;
      top: 0;
    }
  }
}
</style>
